import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { X, ChevronDown, Calendar } from 'lucide-react';
import { Select, DatePicker } from 'antd';
import dayjs from 'dayjs';

const { Option } = Select;

// Constants
const FILTER_OPTIONS = {
  STATUS: ['Upcoming', 'Completed', 'Cancelled', 'Member Missed', 'Counsellor Missed', 'Both Missed', 'Incomplete', 'Unavailable'],
  MODE: ['Video call', 'Audio call', 'In-person', 'Chat'],
  RISK_STATUS: ['No Risk', 'NA', 'Low', 'Moderate', 'High'],
  TYPE: ['New', 'Follow-up']
};

// Reusable class constants
const CLASSES = {
  SECTION: 'flex flex-col gap-3',
  SECTION_TITLE: 'font-poppins font-semibold text-base leading-none text-[#171717] m-0',
  BUTTON_GROUP: 'flex gap-2.5 items-start flex-wrap',
  SEPARATOR: 'w-full h-px bg-[#E2E8F0]',
  DROPDOWN_CONTAINER: 'relative w-full',
  DROPDOWN_DISPLAY: 'border-[1px] border-solid border-[#E2E8F0] hover:border-[#CBD5E1] rounded-xl px-4 py-3 min-h-12 flex items-center justify-between bg-white shadow-sm cursor-pointer',
  INVISIBLE_SELECT: '!absolute !top-0 !left-0 !w-full !h-full !opacity-0 !z-10 [&_.ant-select-selector]:!border-none [&_.ant-select-selector]:!bg-transparent [&_.ant-select-selector]:!h-full'
};

const FilterButton = ({ children, isSelected, onClick, className = '' }) => {
  const baseClasses = 'h-9 px-3 py-1 justify-center items-center gap-1 font-poppins text-[13px] font-medium leading-6 transition-colors rounded-lg border-0';
  const selectedClasses = isSelected
    ? 'bg-[#2563EB] text-white'
    : 'bg-[#EFF6FF] text-[#334155] hover:bg-[#DBEAFE]';
  
  return (
    <button
      onClick={onClick}
      aria-pressed={isSelected}
      aria-label={`Filter by ${children}`}
      className={`${baseClasses} ${selectedClasses} ${className}`}
    >
      {children}
    </button>
  );
};

FilterButton.propTypes = {
  children: PropTypes.node.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string
};

const FilterTag = ({ text, onRemove }) => {
  return (
    <div className="bg-[#EFF6FF] rounded-lg px-2 py-1.5 flex items-center gap-2">
      <span className="text-xs text-[#2563EB] font-medium leading-4">{text}</span>
      <button
        onClick={onRemove}
        className="text-[#64748B] hover:text-[#171717] bg-transparent border-0"
        aria-label={`Remove ${text} filter`}
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

FilterTag.propTypes = {
  text: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired
};

// Reusable MultiSelectDropdown component
const MultiSelectDropdown = ({ label, options, value, onChange, filterKey }) => {
  return (
    <div className={CLASSES.SECTION}>
      <h3 className={CLASSES.SECTION_TITLE}>{label}</h3>
      <div className={CLASSES.DROPDOWN_CONTAINER}>
        <div className={CLASSES.DROPDOWN_DISPLAY}>
          <div className="text-[#64748B]">
            <ChevronDown className="w-5 h-5" />
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {value.length > 0 ? (
              value.map((item) => (
                <FilterTag
                  key={item}
                  text={item}
                  onRemove={() => onChange(filterKey, value.filter(v => v !== item))}
                />
              ))
            ) : null}
          </div>
        </div>
        <Select
          mode="tags"
          placeholder={`Type or select ${label.toLowerCase()}`}
          value={value}
          onChange={(newValue) => onChange(filterKey, Array.isArray(newValue) ? newValue : [newValue])}
          className={CLASSES.INVISIBLE_SELECT}
          showSearch
          tokenSeparators={[',']}
          filterOption={(input, option) => {
            if (!option) return false;
            return option.children.toLowerCase().includes(input.toLowerCase());
          }}
          aria-label={`Select or type ${label.toLowerCase()}`}
        >
          {options.map((option) => (
            <Option key={option} value={option}>
              {option}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );
};

MultiSelectDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  filterKey: PropTypes.string.isRequired
};

function AppointmentFilterSlider({ isOpen, onClose, onApplyFilters, onClearAll, currentFilters }) {
  // Add custom styles for date pickers
  const customStyles = `
    .custom-date-filter .ant-picker-input > input {
      font-size: 16px !important;
    }
  `;

  // Inject styles only when component is open
  React.useEffect(() => {
    if (isOpen) {
      const styleElement = document.createElement('style');
      styleElement.textContent = customStyles;
      styleElement.id = 'appointment-filter-styles';
      document.head.appendChild(styleElement);

      return () => {
        const existingStyle = document.getElementById('appointment-filter-styles');
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }
  }, [isOpen]);

  const [filters, setFilters] = useState({
    type: [],
    status: [],
    mode: [],
    riskStatus: [],
    startDate: null,
    endDate: null
  });

  // Sync with current applied filters when opening
  React.useEffect(() => {
    if (isOpen && currentFilters) {
      setFilters({
        type: currentFilters.type || [],
        status: currentFilters.status || [],
        mode: currentFilters.mode || [],
        riskStatus: currentFilters.riskStatus || [],
        startDate: currentFilters.startDate || null,
        endDate: currentFilters.endDate || null
      });
    }
  }, [isOpen, currentFilters]);


  const handleFilterToggle = useCallback((category, value) => {
    // Only allow single selection per category
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value) ? [] : [value]
    }));
  }, []);

  const handleSelectChange = useCallback((category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: Array.isArray(value) ? value : [value]
    }));
  }, []);

  const handleDateChange = useCallback((dateType, date) => {
    setFilters(prev => ({
      ...prev,
      [dateType]: date ? date.format('YYYY-MM-DD') : null
    }));
  }, []);

  const handleClearAll = useCallback(() => {
    setFilters({
      type: [],
      status: [],
      mode: [],
      riskStatus: [],
      startDate: null,
      endDate: null
    });
    if (onClearAll) onClearAll();
  }, [onClearAll]);

  const handleApply = useCallback(() => {
    if (onApplyFilters) onApplyFilters(filters);
    if (onClose) onClose();
  }, [onApplyFilters, onClose, filters]);

  const handleCancel = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 right-0 h-screen w-[522px] bg-white rounded-l-[24px] z-[1000] shadow-[-4px_0_20px_rgba(0,0,0,0.1)] inline-flex max-md:w-full max-md:rounded-none max-md:left-0 max-md:shadow-none">
      <div className="p-6 h-full flex flex-col gap-6 overflow-y-auto justify-between items-start flex-1 max-md:p-4">
        {/* Header */}
        <div className="flex justify-between items-end w-full max-md:items-center">
          <div className="flex items-center gap-6 max-md:gap-3">
            <button
              onClick={onClose}
              className="bg-none border-none cursor-pointer p-0 w-6 h-6 flex items-center justify-center aspect-square"
              aria-label="Close filter"
            >
              <X className="w-5 h-5 text-[#64748B]" />
            </button>
            <h2 className="font-poppins font-bold text-2xl leading-none text-[#171717] m-0 max-md:text-xl">Appointment Filter</h2>
          </div>
          <button
            onClick={handleClearAll}
            className="font-poppins font-semibold text-sm leading-none text-[#DC2626] bg-transparent border-none cursor-pointer p-0 text-center hover:opacity-80"
            aria-label="Clear all filters"
          >
            Clear all
          </button>
        </div>

        <div className={CLASSES.SEPARATOR} />

        <div className="flex-1 flex flex-col gap-6 max-md:gap-4">
          {/* Date Range */}
          <div className={CLASSES.SECTION}>
            <h3 className={CLASSES.SECTION_TITLE}>Date Range</h3>
            <div className="flex gap-3">
              {/* From Date */}
              <div className="relative w-full">
                <DatePicker
                  placeholder="From"
                  className="custom-date-filter w-full h-10 rounded-xl border-[1px] border-solid border-[#E2E8F0] bg-white shadow-sm font-poppins text-base font-normal"
                  onChange={(date) => handleDateChange('startDate', date)}
                  value={filters.startDate ? dayjs(filters.startDate) : null}
                  aria-label="Select start date"
                />
                <Calendar
                  size={16}
                  className="text-[#64748B] absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                />
              </div>

              {/* To Date */}
              <div className="relative w-full">
                <DatePicker
                  placeholder="To"
                  className="custom-date-filter w-full h-10 rounded-xl border-[1px] border-solid border-[#E2E8F0] bg-white shadow-sm font-poppins text-base font-normal"
                  onChange={(date) => handleDateChange('endDate', date)}
                  value={filters.endDate ? dayjs(filters.endDate) : null}
                  aria-label="Select end date"
                />
                <Calendar
                  size={16}
                  className="text-[#64748B] absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                />
              </div>
            </div>
          </div>

          <div className={CLASSES.SEPARATOR} />

          {/* Type */}
          <div className={CLASSES.SECTION}>
            <h3 className={CLASSES.SECTION_TITLE}>Type</h3>
            <div className={CLASSES.BUTTON_GROUP}>
              {FILTER_OPTIONS.TYPE.map((type) => (
                <FilterButton
                  key={type}
                  isSelected={filters.type.includes(type)}
                  onClick={() => handleFilterToggle('type', type)}
                >
                  {type}
                </FilterButton>
              ))}
            </div>
          </div>

          <div className={CLASSES.SEPARATOR} />

          {/* Status */}
          <div className={CLASSES.SECTION}>
            <h3 className={CLASSES.SECTION_TITLE}>Status</h3>
            <div className={CLASSES.BUTTON_GROUP}>
              {FILTER_OPTIONS.STATUS.map((status) => (
                <FilterButton
                  key={status}
                  isSelected={filters.status.includes(status)}
                  onClick={() => handleFilterToggle('status', status)}
                >
                  {status}
                </FilterButton>
              ))}
            </div>
          </div>

          <div className={CLASSES.SEPARATOR} />

          {/* Mode */}
          <div className={CLASSES.SECTION}>
            <h3 className={CLASSES.SECTION_TITLE}>Mode</h3>
            <div className={CLASSES.BUTTON_GROUP}>
              {FILTER_OPTIONS.MODE.map((mode) => (
                <FilterButton
                  key={mode}
                  isSelected={filters.mode.includes(mode)}
                  onClick={() => handleFilterToggle('mode', mode)}
                >
                  {mode}
                </FilterButton>
              ))}
            </div>
          </div>

          <div className={CLASSES.SEPARATOR} />

          {/* Risk Status */}
          <div className={CLASSES.SECTION}>
            <h3 className={CLASSES.SECTION_TITLE}>Risk Status</h3>
            <div className={CLASSES.BUTTON_GROUP}>
              {FILTER_OPTIONS.RISK_STATUS.map((risk) => (
                <FilterButton
                  key={risk}
                  isSelected={filters.riskStatus.includes(risk)}
                  onClick={() => handleFilterToggle('riskStatus', risk)}
                >
                  {risk}
                </FilterButton>
              ))}
            </div>
          </div>

          <div className={CLASSES.SEPARATOR} />
        </div>

        {/* Footer */}
        <div className="flex w-full flex-col items-start max-md:gap-3">
          <div className={CLASSES.SEPARATOR} />
          <div className="flex items-start gap-4 mt-4 max-md:gap-3 max-md:flex-col max-md:w-full">
            <button onClick={handleCancel} className="flex w-[200px] h-12 px-2.5 py-3 justify-center items-center gap-2 rounded-xl border-[1px] border-solid border-[#E2E8F0] bg-white text-[#334155] font-poppins text-base font-semibold hover:bg-gray-50 transition max-md:w-full max-md:h-12">
              Cancel
            </button>
            <button onClick={handleApply} className="flex w-[200px] h-12 px-4 py-3 justify-center items-center gap-2 rounded-xl btn-gradient border-0 font-poppins text-base font-semibold hover:opacity-95 max-md:w-full max-md:h-12">
              Apply Filter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

AppointmentFilterSlider.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onApplyFilters: PropTypes.func,
  onClearAll: PropTypes.func,
  currentFilters: PropTypes.shape({
    type: PropTypes.arrayOf(PropTypes.string),
    status: PropTypes.arrayOf(PropTypes.string),
    mode: PropTypes.arrayOf(PropTypes.string),
    riskStatus: PropTypes.arrayOf(PropTypes.string),
    startDate: PropTypes.string,
    endDate: PropTypes.string
  })
};

AppointmentFilterSlider.defaultProps = {
  onClose: null,
  onApplyFilters: null,
  onClearAll: null,
  currentFilters: null
};

export default AppointmentFilterSlider;